<script setup lang="ts">
import { onBeforeUnmount, ref, toRefs } from 'vue';

// Piñia
import StoreAuth from '@store/auth';
import Store from '@store/dataProvider';

// Composables
import { useToast } from '../../../composables/useToast';
import { useMessage } from '../../../composables/useMessage';

// Types
import { ITemplateUI } from '../../../types/templatesUI.type';

// Repositories
import TemplatesUIDataProviderRepository from '../../../repositories/templatesUI.repository';
import { useI18n } from 'vue-i18n'
import { resolveLogoUrl } from '@helpers/logo.helpers';

const { t } = useI18n();
const URLBACKEND = import.meta.env.VITE_APP_API;
const store = Store();
const storeAuthProvider = StoreAuth();

const { $toast } = useToast();
const { messageHTML, showMessage } = useMessage();

const templatesUIRepository = new TemplatesUIDataProviderRepository();

const props = defineProps({
  templateUI: {
    type: Object as () => ITemplateUI,
    default: () => ({
      templateUI: {
        name: '',
        urlDomain: '',
        imagePath: '',
        primatyColor: '',
      },
    }),
  },
});

const emit = defineEmits<{
  (e: 'closeModal'): void;
}>();

const { templateUI } = toRefs(props);

const refTableForm = ref<any>(null);

const fields: any = ref([
  {
    key: 'name',
	title: t('global.nombre'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('ajustes.plantillasUI.modalUpdateTemplate.nombre_de_la_plantilla'),
    value: templateUI.value.name,
    cols: 12,
  },
  {
    key: 'urlDomain',
	title: t('ajustes.plantillasUI.modalUpdateTemplate.url_de_dominio'),
    type: 'text',
    validations: {
      rules: {
        required: true,
        verifyExpreRegular: /^(ftp|http|https):\/\/[^ "]+(?<!\/)$/,
      },
	  messages: {
		required: t('global.es_obligatorio'),
	  },
    },
    placeholder: 'https://example.com',
    value: templateUI.value.urlDomain,
    cols: 12,
  },
  {
    key: 'primaryColour',
	title: t('ajustes.plantillasUI.modalUpdateTemplate.color_principal'),
    type: 'color',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		required: t('global.es_obligatorio'),
	  },
    },
	placeholder: t('ajustes.plantillasUI.modalUpdateTemplate.color_principal'),
    value: templateUI.value.primaryColour,
    cols: 12,
  },
  {
    key: 'imagePath',
	title: t('ajustes.plantillasUI.modalUpdateTemplate.logo'),
    type: 'file',
    placeholder: t('ajustes.plantillasUI.modalUpdateTemplate.logo'),
    value: null,
    cols: 12,
  },
]);

const handleOnSubmit = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;

  try {
    const { data } = await templatesUIRepository.updateTemplate(
      templateUI.value.id,
      items
    );
    if (!data) throw new Error();
    let index = store.templatesUI.findIndex(
      (value: any) => value.id === data.id
    );
    if (index > -1) {
      store.setProp({ key: 'templatesUI', index, value: data });
    }
    if (
      storeAuthProvider.user?.template &&
      storeAuthProvider.user.template.id === data.id
    ) {
      storeAuthProvider.setProp({
        key: 'user',
        index: 'template',
        value: data,
      });
    }
    $toast?.success(t('ajustes.plantillasUI.modalUpdateTemplate.plantilla_actualizada_con_exito'));
    emit('closeModal');
  } catch (error: any) {
    showMessage(error.message, 'error');
  }
};

onBeforeUnmount(() => {
  fields.value.forEach((field: any) => {
    field.value = null;
  });
});
</script>

<template>
  <Form ref="refTableForm" :inputs="fields" @onSubmit="handleOnSubmit">
    <template #buttons>
      <p class="label-global">{{t('ajustes.plantillasUI.modalUpdateTemplate.logo_actual')}}</p>
      <img
        class="w-50 m-2 mt-0 background-gray"
        :src="templateUI.imageUrl || resolveLogoUrl(typeof templateUI.imagePath === 'string' ? templateUI.imagePath : '', URLBACKEND)"
        alt="imagen"
      />
      <div v-if="messageHTML" v-html="messageHTML" />
      <div class="d-flex">
        <CButton class="w-100 fs-6 p-2" background="background-blue">
          <template #content>
            <div>
              <span>{{ t("global.actualizar") }}</span>
            </div>
          </template>
        </CButton>
      </div>
    </template>
  </Form>
</template>
